<template>
  <div v-loading="!isDataReady">
    <!-- 工具栏 -->
    <div class="toolbar">
      <SelectUser v-model:selectedMember="selectedMember" ref="selectUser" :element="element"></SelectUser>
      <!--      <el-radio-group v-model="dateType" style="margin-left: 16px">-->
      <!--        <el-radio label="day" :value="1">日</el-radio>-->
      <!--        <el-radio label="week" :value="2">周</el-radio>-->
      <!--        <el-radio label="month" :value="3">月</el-radio>-->
      <!--        <el-radio label="year" :value="4">年</el-radio>-->
      <!--      </el-radio-group>-->
    </div>

    <!-- 图表区域 -->
    <div class="chart-container">
      <div ref="chartRef" class="chart-inner" :style="{ width, height }"></div>
    </div>
  </div>
</template>

<script setup>
import * as echarts from 'echarts';
import {ref, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue';
import SelectUser from "@/views/Home/components/SelectUser.vue";
import {useStore} from "vuex";
import {i18n} from "@/lang";

const store = useStore();
const props = defineProps({
  members: {
    type: Array,
    default: () => []
  },
  width: {
    type: String,
    default: '100%'
  },
  height: {type: String, default: '400px'},
  element: {
    type: Object, default: () => ({})
  }
});

const chartRef = ref(null);
let chartInstance = null;
let resizeObserver = null;
const isDataReady = ref(false)
const selectedMember = ref(null);
const dateType = ref(1);

const lang = computed(() => store.state.system.langLocal);

// 区域列表
const areaList = computed(() => store.state.customer.areaList);

const statisticsTeamCustomerDataVoCountry = computed(() => {
  const stats = store.state.homeEcharts.statisticsTeamCustomerDataVoCountry || [];
  const countries = [
    ...areaList.value,
    {
      countryEn: "Other countries and regions",
      country: "其他国家地区",
      prefix: "99999",
      icon: "",
      id: "99999",
      zoneCode: "Other"
    }
  ];

  return mapCountries(stats, countries, lang.value);
});

function mapCountries(stats, countries, lang) {
  return stats.map(item => {
    const match = countries.find(c => c.country === item.name) || {};
    return {
      ...item,
      ...match, // 合并国家信息
      name: lang === "zh-CN" ? match.country : match.countryEn
    };
  });
}

const pieData = computed(() => statisticsTeamCustomerDataVoCountry.value ?? [])
const chartOption = computed(() => {
  if (!pieData.value.length) {
    // 可选：展示空图表或 return 一个空 option
    return {
      title: {
        text: i18n.global.t('common.data.empty'),
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#ccc',
          fontSize: 16
        }
      }
    }
  }
  return {
    title: {
      left: 'center',
      top: 10,               // ✅ 控制标题与图表的垂直距离
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'item', formatter: function (params) {
        // 截断名称（{b}）为最多10个字符
        const name = params.name.length > 10 ? params.name.slice(0, 10) + '…' : params.name;
        return `${name}: ${params.value} (${params.percent}%)`;
      }
    },
    legend: {
      type: 'scroll',
      orient: 'vertical',     // 垂直方向（因为放在右侧）
      right: 0,               // 靠右对齐
      top: 'middle',          // 垂直居中
      formatter: function (name) {
        return name.length > 10 ? name.slice(0, 10) + '…' : name;
      }
    },
    series: [
      {
        type: 'pie',
        radius: '60%',
        center: ['35%', '50%'], // ✅ 垂直方向位置向下调整，避免贴到标题
        data: pieData.value.map(item => ({
          name: item.name,
          value: item.num // ✅ 将 num 映射为 value
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        label: {
          formatter: function (params) {
            // 截断名称（{b}）为最多10个字符
            const name = params.name.length > 10 ? params.name.slice(0, 10) + '…' : params.name;
            return `${name}\n${params.value} (${params.percent}%)`;
          },
          fontSize: 12,
          lineHeight: 20,
        }
      }
    ]
  }
});


function initChart() {
  if (chartRef.value && !chartInstance) {
    chartInstance = echarts.init(chartRef.value);
  }
}

function updateChart() {
  initChart();
  chartInstance?.clear();
  chartInstance?.setOption(chartOption.value, true);
}

function resizeChart() {
  chartInstance?.resize();
}


async function resetEcharts() {
  isDataReady.value = false
  if (selectedMember.value) {
    await store.dispatch('homeEcharts/getEmailEchartsApis', {
      type: 6,
      dayType: Number(dateType.value),
      teamId: props.element.teamId,
      userId: selectedMember.value === i18n.global.t('common.all') ? '' : selectedMember.value
    });
  }
  isDataReady.value = true
}

watch(() => selectedMember.value, async (userId) => {
  if (userId) {
    await resetEcharts();
    updateChart();
  }
}, {immediate: true})

watch(() => dateType.value, async (newValue) => {
  if (newValue) {
    await resetEcharts();
    updateChart();
  }
}, {immediate: true})

onMounted(async () => {
  await resetEcharts();
  updateChart();
  await store.dispatch('customer/getAreaNames');
  if (chartRef.value) {
    resizeObserver = new ResizeObserver(resizeChart);
    resizeObserver.observe(chartRef.value);
  }
});

onBeforeUnmount(() => {
  chartInstance?.dispose();
  chartInstance = null;
  resizeObserver?.disconnect();
});
</script>


<style scoped lang="less">
.toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

:deep(.el-radio__inner) {
  display: none !important;
}

.chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  overflow: hidden;
}

.chart-inner {
  max-width: 100%;
  width: 100%;
}
</style>